import { useState, useEffect } from 'react';
import { useParams, Navigate, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Skeleton } from '../components/ui/skeleton';
import { useToast } from '../components/ui/use-toast';
import { useBreadcrumb } from '../hooks/useBreadcrumb';
import { gymsService } from '../services/gyms';
import { ZoneManager } from '../components/gym/ZoneManager';
import { GymHoursEditor, type HoursState } from '../components/gym/GymHoursEditor';
import { GymInfoFields, type GymInfoValues } from '../components/gym/GymInfoFields';
import { GymStaffManager } from '../components/gym/GymStaffManager';
import { useAuth } from '../hooks/useAuth';
import type { Gym } from '../types/gym';

const EMPTY_INFO: GymInfoValues = {
  websiteUrl: '',
  address: '',
  city: '',
  zipcode: '',
  latitude: '',
  longitude: '',
};

export default function GymSettings() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { toast } = useToast();
  const { user } = useAuth();

  const [gym, setGym] = useState<Gym | null>(null);
  const [loading, setLoading] = useState(true);

  // Get current user staff info for this gym from user profile
  const currentUserStaff = user?.staff?.find(staff => staff.gym_slug === slug) || null;

  // Info form state
  const [climbNameSuggestions, setClimbNameSuggestions] = useState('');
  const [info, setInfo] = useState<GymInfoValues>(EMPTY_INFO);
  const [hours, setHours] = useState<HoursState>({});
  const [isSavingInfo, setIsSavingInfo] = useState(false);

  useEffect(() => {
    if (!slug) return;

    let isMounted = true;
    const fetchData = async () => {
      setLoading(true);
      try {
        const gymData = await gymsService.getGymBySlug(slug);

        if (isMounted) {
          setGym(gymData);

          // Initialize info form from gym data
          setClimbNameSuggestions(gymData.climb_name_suggestions || '');
          setInfo({
            websiteUrl: gymData.website_url || '',
            address: gymData.address || '',
            city: gymData.city || '',
            zipcode: gymData.zipcode || '',
            latitude: gymData.latitude != null ? String(gymData.latitude) : '',
            longitude: gymData.longitude != null ? String(gymData.longitude) : '',
          });
          setHours(gymData.hours || {});
        }
      } catch (error) {
        console.error('Failed to fetch gym data:', error);
        if (isMounted) {
          toast({
            title: t('gymSettings.failedToLoad'),
            description: error instanceof Error ? error.message : t('errors.unexpectedError'),
            variant: 'destructive',
          });
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchData();
    return () => {
      isMounted = false;
    };
  }, [slug, t, toast]);

  const handleSaveInfo = async () => {
    if (!gym || !slug) return;

    setIsSavingInfo(true);
    try {
      const updatedGym = await gymsService.updateGym(slug, {
        climb_name_suggestions: climbNameSuggestions || undefined,
        website_url: info.websiteUrl || undefined,
        address: info.address || undefined,
        city: info.city || undefined,
        zipcode: info.zipcode || undefined,
        latitude: info.latitude ? parseFloat(info.latitude) : undefined,
        longitude: info.longitude ? parseFloat(info.longitude) : undefined,
        hours: Object.keys(hours).length > 0 ? hours : undefined,
      });
      setGym(updatedGym);
      toast({
        title: t('gymSettings.infoSaved'),
        description: t('gymSettings.infoSavedDescription'),
      });
    } catch (error) {
      console.error('Failed to save gym info:', error);
      toast({
        title: t('gymSettings.saveInfoError'),
        description: error instanceof Error ? error.message : t('errors.unexpectedError'),
        variant: 'destructive',
      });
    } finally {
      setIsSavingInfo(false);
    }
  };

  useBreadcrumb(
    () => ({
      onBack: () => navigate(-1),
      segments: [
        { label: gym?.name ?? '', onClick: () => navigate(`/gyms/${slug}`) },
        { label: t('gymSettings.title') },
      ],
      loading,
    }),
    [gym?.name, slug, loading, t]
  );

  if (!slug) {
    return <Navigate to="/not-found" replace />;
  }

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto space-y-6">
        <Card>
          <CardContent className="pt-6">
            <Skeleton className="h-48 w-full" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!gym || !currentUserStaff) {
    return <Navigate to="/not-found" replace />;
  }

  const isAdmin = currentUserStaff.staff_roles.includes('admin') && currentUserStaff.status === 'confirmed';

  if (!isAdmin) {
    return <Navigate to={`/gyms/${slug}`} replace />;
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Gym Info */}
      <Card>
        <CardHeader>
          <CardTitle>{t('gymSettings.infoSection')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <GymInfoFields values={info} onChange={(patch) => setInfo((prev) => ({ ...prev, ...patch }))} />

          <GymHoursEditor value={hours} onChange={setHours} />

          {/* Climb name suggestions */}
          <div className="space-y-2">
            <Label htmlFor="climbNameSuggestions">{t('gymSettings.climbNameSuggestions')}</Label>
            <p className="text-xs text-muted-foreground">{t('gymSettings.climbNameSuggestionsHelper')}</p>
            <Textarea
              id="climbNameSuggestions"
              rows={6}
              placeholder={t('gymSettings.climbNameSuggestionsPlaceholder')}
              value={climbNameSuggestions}
              onChange={(e) => setClimbNameSuggestions(e.target.value)}
            />
          </div>

          <Button onClick={handleSaveInfo} disabled={isSavingInfo}>
            {isSavingInfo ? t('gymSettings.saving') : t('gymSettings.saveInfo')}
          </Button>
        </CardContent>
      </Card>

      {/* Zone Management */}
      <Card>
        <CardContent className="pt-6">
          <ZoneManager locationSlug={slug} isStaffMember={true} />
        </CardContent>
      </Card>

      {/* Staff Management */}
      <GymStaffManager gymId={gym.id} currentUserSlug={user?.slug} />
    </div>
  );
}
